import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import SuperuserModeBanner from './SuperuserModeBanner';
import { SupportContextProvider } from '../contexts/SupportContext';
import { ChatTray } from './chat/ChatTray';
import { BreadcrumbProvider } from './breadcrumbs/BreadcrumbProvider';
import { PageHeading } from './breadcrumbs/PageHeading';
import { FeatureFlag } from '../constants/featureFlags';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';

// Non-EMR surfaces that share this layout; the superuser banner stays off them.
const NON_EMR_PATH = /^\/(rogerdoc|resources)(\/|$)/;

const Layout: React.FC = () => {
  const { pathname } = useLocation();
  const showSuperuserBanner = !NON_EMR_PATH.test(pathname);
  const {
    isSidebarCollapsed,
    isMobileSidebarOpen,
    setMobileSidebarOpen,
    chatEnabled,
  } = useStore(
    useShallow(state => ({
      isSidebarCollapsed: state.isSidebarCollapsed,
      isMobileSidebarOpen: state.isMobileSidebarOpen,
      setMobileSidebarOpen: state.setMobileSidebarOpen,
      chatEnabled: state.hasFeatureFlag(FeatureFlag.CHAT, 'workos'),
    }))
  );
  const mobileDrawerRef = useRef<HTMLDivElement>(null);

  // Mount the drawer's Sidebar only after the first open: a second always-
  // mounted Sidebar duplicates mount effects (e.g. AgencySwitcher's
  // /auth/organizations fetch) on every page load, including desktop.
  const [hasOpenedMobileSidebar, setHasOpenedMobileSidebar] = useState(false);
  useEffect(() => {
    if (isMobileSidebarOpen) {
      setHasOpenedMobileSidebar(true);
    }
  }, [isMobileSidebarOpen]);

  // Lock body scroll while the drawer is open (iOS/Android Safari otherwise
  // scrolls the page under the overlay).
  useEffect(() => {
    if (!isMobileSidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileSidebarOpen]);

  // While the drawer is open: move focus into it, close on Escape, and trap Tab
  // focus inside the panel. It's an aria-modal dialog, so focus must not reach
  // the page behind the overlay. A manual trap (rather than the `inert`
  // attribute) keeps this working on older mobile Safari, which lacks `inert`.
  useEffect(() => {
    if (!isMobileSidebarOpen) return;
    const drawer = mobileDrawerRef.current;
    if (!drawer) return;

    const getFocusable = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => el.offsetParent !== null);

    // Move focus into the panel once it can accept it. The just-shown panel can
    // reject focus for a frame or two (and the toggle button that opened it keeps
    // focus otherwise), so retry briefly until focus actually lands inside.
    let focusTimer = 0;
    let attempts = 0;
    const moveFocusIn = () => {
      (getFocusable()[0] ?? drawer).focus();
      if (!drawer.contains(document.activeElement) && attempts++ < 10) {
        focusTimer = window.setTimeout(moveFocusIn, 16);
      }
    };
    focusTimer = window.setTimeout(moveFocusIn, 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileSidebarOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        e.preventDefault();
        drawer.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        // Wrap backward off the first element (or back in if focus escaped).
        if (active === first || active === drawer || !drawer.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !drawer.contains(active)) {
        // Wrap forward off the last element (or back in if focus escaped).
        e.preventDefault();
        first.focus();
      }
    };
    // Capture phase so the trap runs before nav-item key handlers.
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isMobileSidebarOpen, setMobileSidebarOpen]);

  return (
    /* Rail is in flow, not fixed, so it bounces with the page on overscroll. */
    <div className='flex h-screen bg-background'>
      {/* Sidebar — desktop/tablet */}
      <div
        className={`hidden md:flex ${isSidebarCollapsed ? 'w-16' : 'w-52'} shrink-0 flex-col overflow-hidden border-r bg-background transition-all duration-300`}
      >
        <Sidebar />
      </div>
      {/* Mobile off-canvas sidebar */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${isMobileSidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={() => setMobileSidebarOpen(false)}
        aria-hidden='true'
      />
      {/* `invisible` when closed keeps the off-canvas panel out of the tab
          order and accessibility tree (React 18 has no `inert` prop);
          visibility is transitioned so the slide-out animation still plays. */}
      <div
        ref={mobileDrawerRef}
        id='mobile-navigation'
        role='dialog'
        aria-modal={isMobileSidebarOpen}
        aria-label='Navigation'
        aria-hidden={!isMobileSidebarOpen}
        tabIndex={-1}
        className={`md:hidden fixed inset-y-0 left-0 z-50 flex w-64 max-w-[85vw] flex-col overflow-hidden border-r bg-background transition-[transform,visibility] duration-300 outline-none ${isMobileSidebarOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'}`}
      >
        {(isMobileSidebarOpen || hasOpenedMobileSidebar) && (
          <Sidebar mobile onNavigate={() => setMobileSidebarOpen(false)} />
        )}
      </div>
      {/* min-w-0: wide pages scroll inside `main` instead of pushing the rail. */}
      <div className='flex h-full min-w-0 flex-1 flex-col'>
        <SupportContextProvider>
          {showSuperuserBanner && <SuperuserModeBanner />}
          <BreadcrumbProvider>
            <Header />
            {/* Bottom padding is --layout-main-pb (index.css). With chat on,
                data-chat-clearance adds Messages launcher clearance on top of
                the base padding app-wide. PatientDetail's visit view cancels
                only --layout-main-pb-base so chat clearance remains. */}
            <main
              {...(chatEnabled ? { 'data-chat-clearance': '' } : {})}
              className='flex-1 overflow-y-auto px-2 pt-2 sm:px-4 sm:pt-4 md:px-6 md:pt-6 pb-[length:var(--layout-main-pb)]'
            >
              <PageHeading />
              <Outlet />
            </main>
          </BreadcrumbProvider>
        </SupportContextProvider>
        <ChatTray />
      </div>
    </div>
  );
};

export default Layout;
